/**
 * Draw grouping — collapse multiple lab files that belong to the same blood draw
 * into a single logical "draw group".
 *
 * Quest (via Epic MyChart) exports one sheet per panel, so a single draw arrives
 * as N separate files that all share a testdate. The frontend keys tests by file,
 * which would render N pills for one draw. These helpers regroup the flat file
 * list by (testdate, testtime) so the UI shows one pill per draw.
 *
 * Time semantics (see xdesign_docs/MULTI_DATE_LAB_INGEST_AND_MERGE.md §11.3):
 *   - all times null on a date                -> 1 group, no time shown
 *   - one explicit time + nulls               -> 1 group (nulls absorbed), no time shown
 *   - all files share one explicit time       -> 1 group, no time shown
 *   - 2+ distinct explicit times on a date    -> 1 group per time, time shown
 *     (files with a null time on such a date cannot be attributed to either
 *      time, so they get their own untimed group)
 */

const NO_DATE = '__nodate__';

/**
 * Normalize a raw collection time to "HH:MM", or null when absent/meaningless.
 * "00:00" is treated as absent — it is the upload lambda's placeholder when the
 * source document carried no collection time.
 */
export function normalizeTestTime(raw) {
    if (raw == null) return null;
    const str = String(raw).trim();
    if (!str) return null;

    // "8:30 AM" / "08:30 pm"
    const ampm = str.match(/^(\d{1,2})[:_.](\d{2})\s*([ap])\.?m\.?$/i);
    if (ampm) {
        let hh = parseInt(ampm[1], 10) % 12;
        if (ampm[3].toLowerCase() === 'p') hh += 12;
        const norm = `${String(hh).padStart(2, '0')}:${ampm[2]}`;
        return norm === '00:00' ? null : norm;
    }

    // "08:30", "8_30", "0830"
    const hm = str.match(/^(\d{1,2})[:_.]?(\d{2})$/);
    if (!hm) return null;
    const hh = parseInt(hm[1], 10);
    const mm = parseInt(hm[2], 10);
    if (hh > 23 || mm > 59) return null;
    const norm = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    return norm === '00:00' ? null : norm;
}

/**
 * Pull the collection time off a file, preferring the header and falling back to
 * the filename pattern `..._blood_<YYYYMMDD>_<HH:MM>_...`.
 */
export function getFileTestTime(file) {
    const fromHeader = normalizeTestTime(file?.data?.header?.testtime ?? file?.header?.testtime);
    if (fromHeader) return fromHeader;
    const m = file?.key?.match(/_blood_\d{8}_(\d{1,2})[:_](\d{2})/);
    return m ? normalizeTestTime(`${m[1]}:${m[2]}`) : null;
}

function mergeBodies(files, normalizeTestName) {
    const seen = new Set();
    const body = [];
    for (const file of files) {
        const entries = file?.data?.body;
        if (!Array.isArray(entries)) continue;
        for (const entry of entries) {
            const raw = entry?.alias || entry?.test_name;
            const name = normalizeTestName && raw ? normalizeTestName(raw) : raw;
            // Entries with no identifiable name can't be deduped — keep them all.
            if (!name) { body.push(entry); continue; }
            const dedupeKey = String(name).toLowerCase();
            if (seen.has(dedupeKey)) continue;
            seen.add(dedupeKey);
            body.push(entry);
        }
    }
    return body;
}

function mergePanels(files) {
    const panels = [];
    for (const file of files) {
        const p = file?.data?.header?.panels;
        for (const name of Array.isArray(p) ? p : (p ? [p] : [])) {
            if (!panels.includes(name)) panels.push(name);
        }
    }
    return panels;
}

/**
 * Build a group pseudo-file. Single-member groups return the original file object
 * untouched (with the grouping metadata attached) so nothing downstream that
 * relies on file identity or `key` changes behaviour for existing users.
 */
function buildGroup(files, testdate, testtime, showTime, normalizeTestName) {
    const sourceKeys = files.map(f => f.key).filter(Boolean);

    if (files.length === 1) {
        const file = files[0];
        return { ...file, testdate, testtime, showTime, sourceKeys, sourceFiles: files };
    }

    const base = files[0];
    return {
        ...base,
        key: base.key,
        data: {
            ...base.data,
            header: {
                ...base.data?.header,
                testdate,
                testtime,
                panels: mergePanels(files),
            },
            body: mergeBodies(files, normalizeTestName),
        },
        testdate,
        testtime,
        showTime,
        sourceKeys,
        sourceFiles: files,
    };
}

/**
 * Group a flat, blood-test-only file list into one entry per draw.
 * Returns a new array sorted by testdate ascending, then collection time
 * ascending (untimed groups last within a date). Undated files sort last.
 *
 * @param {Array} files - file objects shaped { key, data: { header, body } }
 * @param {Function} [normalizeTestName] - optional canonical-name resolver used
 *        when deduping markers that appear on more than one panel sheet
 * @returns {Array} group pseudo-files, each also carrying
 *        { testdate, testtime, showTime, sourceKeys, sourceFiles }
 */
export function groupFilesByDraw(files, normalizeTestName) {
    if (!Array.isArray(files)) return [];

    const byDate = new Map();
    for (const file of files) {
        const testdate = file?.data?.header?.testdate ?? null;
        // An undated file has no draw identity, so it can only group with itself.
        // Bucketing them all under one key made every report whose date the parser
        // could not read — a state the ingest lambda deliberately produces rather
        // than inventing today's date — collapse into a single "No Date" pill that
        // showed one file's values and hid the rest.
        const dateKey = testdate == null ? `${NO_DATE}:${file?.key ?? ''}` : String(testdate);
        if (!byDate.has(dateKey)) byDate.set(dateKey, { testdate, files: [] });
        byDate.get(dateKey).files.push(file);
    }

    const groups = [];
    for (const { testdate, files: dateFiles } of byDate.values()) {
        // Stable, deterministic order regardless of how S3 listed the objects.
        const ordered = [...dateFiles].sort((a, b) => String(a.key || '').localeCompare(String(b.key || '')));

        const timed = new Map();
        const untimed = [];
        for (const file of ordered) {
            const time = getFileTestTime(file);
            if (time == null) { untimed.push(file); continue; }
            if (!timed.has(time)) timed.set(time, []);
            timed.get(time).push(file);
        }

        const distinctTimes = [...timed.keys()].sort();

        if (distinctTimes.length <= 1) {
            // Zero or one explicit time: everything on this date is one draw, and
            // the time is never shown because there is nothing to disambiguate.
            const time = distinctTimes.length ? distinctTimes[0] : null;
            groups.push(buildGroup(ordered, testdate, time, false, normalizeTestName));
            continue;
        }

        // Two or more distinct times: each becomes its own pill, with the time shown.
        for (const time of distinctTimes) {
            groups.push(buildGroup(timed.get(time), testdate, time, true, normalizeTestName));
        }
        if (untimed.length) {
            // Cannot be attributed to any of the explicit times — its own untimed pill.
            groups.push(buildGroup(untimed, testdate, null, true, normalizeTestName));
        }
    }

    return groups.sort((a, b) => {
        const ad = a.testdate == null ? Infinity : Number(a.testdate);
        const bd = b.testdate == null ? Infinity : Number(b.testdate);
        if (ad !== bd) return ad - bd;
        if (a.testtime === b.testtime) return 0;
        if (a.testtime == null) return 1;
        if (b.testtime == null) return -1;
        return a.testtime.localeCompare(b.testtime);
    });
}

/** Render "8:30 AM" from "08:30". */
export function formatTestTime(time) {
    const norm = normalizeTestTime(time);
    if (!norm) return '';
    const [hhStr, mm] = norm.split(':');
    const hh = parseInt(hhStr, 10);
    const suffix = hh < 12 ? 'AM' : 'PM';
    const display = hh % 12 === 0 ? 12 : hh % 12;
    return `${display}:${mm} ${suffix}`;
}

/**
 * Pill label for a draw group: the date, plus the collection time only when the
 * group is one of several distinct times on that date.
 *
 * @param {Object} group - entry from groupFilesByDraw (a plain file also works)
 * @param {Function} formatDate - date formatter (GenericUtil.formatDate)
 */
export function formatDrawLabel(group, formatDate) {
    const testdate = group?.testdate ?? group?.data?.header?.testdate;
    if (!testdate) return group?.data?.header?.isTemp ? 'New Test' : 'No Date';

    const label = formatDate(testdate.toString());
    if (!group?.showTime) return label;

    const time = formatTestTime(group.testtime);
    return time ? `${label} · ${time}` : label;
}
